import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Megaphone, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_META = {
  info: { label: 'Info', badge: 'bg-slate-100 text-slate-700', ring: 'border-slate-200', bg: 'bg-white' },
  warning: { label: 'Warning', badge: 'bg-amber-100 text-amber-800', ring: 'border-amber-200', bg: 'bg-amber-50/40' },
  urgent: { label: 'Urgent', badge: 'bg-red-100 text-red-700', ring: 'border-red-300', bg: 'bg-red-50/40' },
};

const MyAnnouncements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const highlightId = params.get('id');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/announcements/mine`, { withCredentials: true });
      setItems(r.data);
    } catch { toast.error('Could not load announcements'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const acknowledge = async (a) => {
    try {
      await axios.post(`${API}/announcements/${a.id}/acknowledge`, {}, { withCredentials: true });
      toast.success('Acknowledged');
      fetchAll();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast.error('Your session expired — please log in again.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed');
      }
    }
  };

  const unreadCount = items.filter((a) => !a.acknowledged).length;
  const urgentUnread = items.filter((a) => !a.acknowledged && a.priority === 'urgent');

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="my-announcements-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-[#002FA7]" />
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Announcements
              </h1>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {unreadCount === 0 ? "You're all caught up." : `${unreadCount} announcement${unreadCount === 1 ? '' : 's'} to review.`}
            </p>
          </div>
        </div>

        {urgentUnread.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 flex items-center gap-2" data-testid="urgent-banner">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-900">
              <strong>{urgentUnread.length}</strong> urgent announcement{urgentUnread.length === 1 ? '' : 's'} awaiting acknowledgement.
            </span>
          </div>
        )}

        {loading ? (
          <div className="p-10 text-center text-slate-500 bg-white border border-slate-200 rounded-md">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 bg-white border border-slate-200 rounded-md" data-testid="my-announcements-empty">
            No announcements yet.
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((a) => {
              const pri = PRIORITY_META[a.priority] || PRIORITY_META.info;
              const isHighlighted = highlightId === a.id;
              return (
                <li
                  key={a.id}
                  data-testid={`my-announcement-${a.id}`}
                  className={`${pri.bg} border ${pri.ring} ${isHighlighted ? 'ring-2 ring-[#002FA7]' : ''} rounded-md p-4`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${pri.badge}`}>{pri.label}</span>
                        <h3 className="text-base font-semibold text-slate-900">{a.title}</h3>
                        {a.acknowledged && (
                          <span className="text-xs text-emerald-700 flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Read
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2">{a.body}</p>
                      <p className="text-xs text-slate-500 mt-3">Posted {new Date(a.created_at).toLocaleString()}</p>
                    </div>
                    {!a.acknowledged && (
                      <button
                        onClick={() => acknowledge(a)}
                        data-testid={`ack-${a.id}`}
                        className="shrink-0 bg-[#002FA7] text-white text-xs font-semibold rounded-md px-3 py-1.5 hover:bg-[#00247D]"
                      >
                        Acknowledge
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyAnnouncements;
